<!-- @format -->
<template>
    <div class="landing-page-form-activity-widget">
        <div class="widget-contents-container">
            <loading-card :is-loading="isLoading" />
            <div class="widget-header">
                <h3>Form Activity</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="!isLoading">
                    <div class="section">
                        <div class="section-label">Form Submission</div>
                        <div class="section-container">
                            <label>Form Submission Date</label>
                            <div class="section-date">
                                <p class="date">
                                    {{ formSubmissionData.createdAt | momentDateMMMDYYYY }}
                                </p>
                                <p class="hour">
                                    {{ formSubmissionData.createdAt | momentTime }}
                                </p>
                            </div>
                            <label>Form Name</label>
                            <p>{{ formSubmissionData.name || 'N/A' }}</p>
                        </div>
                    </div>
                    <div
                        v-for="({ label, fields }, idx) in formSections"
                        :key="idx"
                        class="section"
                    >
                        <div v-if="fields.length > 0">
                            <div class="section-label">{{ label }}</div>
                            <div class="section-container">
                                <div class="info">
                                    <div class="info-wrapper">
                                        <div
                                            v-for="({ isUrl, name, value }, id) in fields"
                                            :key="id"
                                            class="field-info"
                                        >
                                            <label>{{ name }}</label>
                                            <div v-for="(data, i) in value" :key="i">
                                                <a
                                                    v-if="isUrl"
                                                    :href="data"
                                                    target="_blank"
                                                    class="website-link"
                                                >
                                                    <app-tooltip :value="data" />
                                                </a>
                                                <p v-else>{{ data }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div v-if="!isLoading" class="action-container">
                    <app-button v-if="formSubmissionData.contactId" @click="goToContact"
                        >Go to Contact</app-button
                    >
                    <app-button v-if="formSubmissionData.companyId" @click="goToCompany"
                        >Go to Company</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { LandingPageFormSubmissionService } from '@/scripts/services/landing-page/landing-page-form-submission-service';
import { FormService } from '@/scripts/services/form/form-service';

const AppButton = () => import('@/components/common/app-button.vue');
const AppTooltip = () => import('@/components/common/app-tooltip');
const LoadingCard = () => import('@/components/common/loading-card');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'landing-page-form-activity-widget',

    components: {
        'app-button': AppButton,
        'app-tooltip': AppTooltip,
        'loading-card': LoadingCard,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            formSubmissionData: {
                formId: null,
                name: null,
                createdAt: null,
                contactId: null,
                companyId: null,
            },

            formSections: [],

            isLoading: true,
        };
    },

    created() {
        this.isLoading = true;
        this.getLandingPageFormSubmission().then(() => {
            this.getLandingPageForm().finally(() => {
                this.isLoading = false;
            });
        });
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToContact() {
            this.closeWidget();
            this.$router.push(`/Contacts/${this.formSubmissionData.contactId}`);
        },

        goToCompany() {
            this.closeWidget();
            this.$router.push(`/Companies/${this.formSubmissionData.companyId}`);
        },

        getLandingPageFormSubmission() {
            return new Promise((resolve) => {
                LandingPageFormSubmissionService.get(this.widget.landingPageFormActivityId)
                    .then(({ data }) => {
                        const { ContactId, CompanyId, CreatedAt, FormId } = data;

                        const formFields = JSON.parse(data.FormData);
                        this.formSections = this.mapFormFields(formFields);

                        this.formSubmissionData.createdAt = this.$moment.utc(CreatedAt);
                        this.formSubmissionData.formId = FormId;

                        this.formSubmissionData.contactId = ContactId;
                        this.formSubmissionData.companyId = CompanyId;

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your form submission information.',
                            { error },
                        );
                        this.closeWidget();
                    });
            });
        },

        getLandingPageForm() {
            return new Promise((resolve) => {
                FormService.get(this.formSubmissionData.formId)
                    .then(({ data }) => {
                        const [form] = data.value;
                        this.formSubmissionData.name = form.Name;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your form information.',
                            { error },
                        );
                        this.closeWidget();
                    });
            });
        },

        mapFormFields(fields) {
            const defaultFieldTypes = {
                contact: [],
                company: [],
                general: [],
            };
            const fieldSections = [];

            const fieldsByType = fields.reduce((acc, { Label, Type, Name, Value }) => {
                if (Value.some((data) => data !== null && data.length > 0)) {
                    acc[Type].push({
                        name: Label,
                        isUrl: Name === 'FileUpload',
                        value: Value,
                    });
                }
                return acc;
            }, defaultFieldTypes);

            for (const type in fieldsByType) {
                fieldSections.push({
                    label: type.charAt(0).toUpperCase() + type.slice(1) + ' Information',
                    fields: fieldsByType[type],
                });
            }

            return fieldSections;
        },
    },
};
</script>

<style>
.landing-page-form-activity-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.landing-page-form-activity-widget .cancel-btn:hover {
    background: #e1e0e5;
}

.landing-page-form-activity-widget .section {
    margin-bottom: 1em;
}

.landing-page-form-activity-widget .section-label {
    position: relative;
    bottom: -12px;
    width: fit-content;
    background: #ffffff;
    margin: 0 auto;
    font-weight: 500;
    padding: 0 47px;
}

.landing-page-form-activity-widget .section-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    padding: 22px 32px;
}

.landing-page-form-activity-widget .section-date {
    display: flex;
    flex-direction: row;
}

.landing-page-form-activity-widget .section-date .date {
    border-right: 2px solid #dcdbe0;
    margin-right: 17px;
    padding-right: 17px;
}

.landing-page-form-activity-widget .hour {
    font-weight: bold;
}

.landing-page-form-activity-widget .field-info {
    margin-bottom: 18px;
}

.landing-page-form-activity-widget .field-info p {
    margin-bottom: 0;
}

.landing-page-form-activity-widget .action-container {
    display: flex;
    flex-direction: row;
    gap: 13px;
}

.landing-page-form-activity-widget .loading-card {
    left: -110px;
}
</style>
